import cls from './table.module.scss';
import { SkeletonRows } from 'shared/ui/skeleton/skeleton-rows';
import { TableProps } from './table.types';

const LOADING_ROW_COUNT = 3;

const cellClassName = (
  column: { className?: string },
  isFirst: boolean
): string => {
  return [cls.cell, isFirst ? cls.firstCell : '', column.className ?? '']
    .filter(Boolean)
    .join(' ');
};

export const Table = <T,>({
  columns,
  rows,
  getRowKey,
  isLoading,
  emptyState,
  onRowClick,
  footer,
}: TableProps<T>) => {
  const hasColumnFooter = columns.some(column => column.renderFooter);
  return (
    <table className={cls.table}>
      <colgroup>
        {columns.map(column => (
          <col
            key={column.id}
            style={column.width ? { width: column.width } : undefined}
          />
        ))}
      </colgroup>
      <thead>
        <tr className={cls.headerRow}>
          {columns.map((column, index) => (
            <th
              key={column.id}
              style={column.align ? { textAlign: column.align } : undefined}
              className={`${cls.headerCell}${index === 0 ? ` ${cls.headerFirstCell}` : ''}`}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <SkeletonRows
            count={LOADING_ROW_COUNT}
            colSpan={columns.length}
            cellClassName={cls.loadingCell}
            height={40}
          />
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className={cls.emptyCell}>
              {emptyState}
            </td>
          </tr>
        ) : (
          rows.map(row => (
            <tr
              key={getRowKey(row)}
              className={`${cls.row}${onRowClick ? ` ${cls.clickableRow}` : ''}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((column, index) => (
                <td
                  key={column.id}
                  style={column.align ? { textAlign: column.align } : undefined}
                  className={cellClassName(column, index === 0)}
                >
                  {column.renderCell(row)}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
      {footer ? (
        <tfoot>
          <tr className={cls.footerRow}>
            <td colSpan={columns.length} className={cls.cell}>
              {footer}
            </td>
          </tr>
        </tfoot>
      ) : (
        hasColumnFooter && (
          <tfoot>
            <tr className={cls.footerRow}>
              {columns.map((column, index) => (
                <td
                  key={column.id}
                  style={column.align ? { textAlign: column.align } : undefined}
                  className={`${cls.cell}${index === 0 ? ` ${cls.footerFirstCell}` : ''}${column.className ? ` ${column.className}` : ''}`}
                >
                  {column.renderFooter?.()}
                </td>
              ))}
            </tr>
          </tfoot>
        )
      )}
    </table>
  );
};
